// src/components/ClientList/ClientList.js
import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Redirect } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { apiBaseUrl } from '../../util/api';
import { queryOwnListings } from '../../containers/ManageListingsPage/ManageListingsPage.duck';
import { getOwnListingsById } from '../../containers/ManageListingsPage/ManageListingsPage.duck';
import AddClientModal from './AddClientModal';
import BulkAddClientModal from './BulkAddClientModal';
import ClientCard from './ClientCard';
import css from './ClientList.module.css';


// ── Email Campaign Modal ─────────────────────────────────────
const EmailCampaignModal = ({ clients, onClose }) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) return;
    setSending(true);
    try {
      const res = await fetch(`${apiBaseUrl()}/api/clients/send-campaign`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientIds: clients.map(c => c.id), subject: subject.trim(), body: body.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setResult({ sent: data.sent, failed: data.failed });
      } else {
        setResult({ error: data.error || 'Failed to send' });
      }
    } catch {
      setResult({ error: 'Network error — please try again' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={css.modalOverlay} onClick={onClose}>
      <div className={css.modal} onClick={e => e.stopPropagation()}>
        <div className={css.modalHeader}>
          <h2 className={css.modalTitle}>Send Email Campaign</h2>
          <button className={css.closeButton} onClick={onClose}>×</button>
        </div>
        <div className={css.modalBody}>
          {result ? (
            <div className={css.campaignResult}>
              {result.error ? (
                <p className={css.errorBox}>{result.error}</p>
              ) : (
                <p className={css.campaignSuccess}>
                  ✅ Sent to {result.sent} client{result.sent !== 1 ? 's' : ''}
                  {result.failed > 0 && ` (${result.failed} failed)`}
                </p>
              )}
              <button className={css.btnPrimary} onClick={onClose}>Done</button>
            </div>
          ) : (
            <>
              <p className={css.modalDescription}>
                Sending to <strong>{clients.length}</strong> client{clients.length !== 1 ? 's' : ''}.{' '}
                Use <code>{'{firstName}'}</code> to personalize.
              </p>
              <div className={css.formGroup}>
                <label className={css.label}>Subject</label>
                <input
                  className={css.input}
                  type="text"
                  placeholder="e.g. Special offer just for you"
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                />
              </div>
              <div className={css.formGroup}>
                <label className={css.label}>Message</label>
                <textarea
                  className={css.textarea}
                  rows={8}
                  placeholder={`Hi {firstName},\n\nI wanted to reach out with a special message for you…`}
                  value={body}
                  onChange={e => setBody(e.target.value)}
                />
              </div>
            </>
          )}
        </div>
        {!result && (
          <div className={css.modalFooter}>
            <button className={css.btnSecondary} onClick={onClose}>Cancel</button>
            <button
              className={css.btnPrimary}
              onClick={handleSend}
              disabled={sending || !subject.trim() || !body.trim()}
            >
              {sending ? 'Sending…' : `Send to ${clients.length} client${clients.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Sort helper ──────────────────────────────────────────────
const sortClients = (clients, key, dir) => {
  if (!key) return clients;
  return [...clients].sort((a, b) => {
    let av = a[key] ?? '';
    let bv = b[key] ?? '';
    if (key === 'last_name') {
      av = `${a.last_name} ${a.first_name}`.toLowerCase();
      bv = `${b.last_name} ${b.first_name}`.toLowerCase();
    } else if (typeof av === 'string') {
      av = av.toLowerCase();
      bv = bv.toLowerCase();
    }
    if (av < bv) return dir === 'asc' ? -1 : 1;
    if (av > bv) return dir === 'asc' ? 1 : -1;
    return 0;
  });
};

// ── Main ClientList component ────────────────────────────────
const ClientList = ({ currentUser }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [error, setError] = useState(null);
  const [showListingModal, setShowListingModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [showEmailCampaign, setShowEmailCampaign] = useState(false);
  const [dmNavigating, setDmNavigating] = useState(false);
  const [limitBanner, setLimitBanner] = useState(null); // { limit, message }

  // CR-3: density toggle
  const [density, setDensity] = useState('comfortable');

  // CR-6: sort state
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  // CR-7: row selection
  const [selectedIds, setSelectedIds] = useState(new Set());

  const searchTimer = useRef(null);

  const userType = currentUser?.attributes?.profile?.protectedData?.userType;

  // Get listings from Redux
  const { currentPageResultIds } = useSelector(state => state.ManageListingsPage || { currentPageResultIds: [] });
  const allListings = useSelector(state => getOwnListingsById(state, currentPageResultIds));
  const publishedListings = allListings.filter(l => l.attributes?.state === 'published');

  if (currentUser && userType === 'client') return <Redirect to="/my-programs" />;

  useEffect(() => {
    // Silent background sync on mount — upgrades any prospect with an accepted booking to active
    fetch(`${apiBaseUrl()}/api/clients/sync`, { method: 'POST', credentials: 'include' })
      .then(r => r.json())
      .then(data => { if (data.success) fetchClients(); })
      .catch(() => {}); // silent — never surface errors to user
  }, []); // eslint-disable-line

  useEffect(() => {
    fetchClients();
  }, [statusFilter]);

  useEffect(() => {
    if (currentPageResultIds.length === 0) {
      dispatch(queryOwnListings({ page: 1 }));
    }
  }, [dispatch, currentPageResultIds.length]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      const response = await fetch(`${apiBaseUrl()}/api/clients?${params}`, { credentials: 'include' });
      const data = await response.json();
      if (data.success) {
        setClients(data.clients);
        setSelectedIds(new Set());
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleAddClient = async clientData => {
    try {
      const response = await fetch(`${apiBaseUrl()}/api/clients`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(clientData),
      });
      const data = await response.json();
      if (data.success) {
        setShowAddModal(false);
        fetchClients();
      } else if (data.errorCode === 'client_limit_reached') {
        setShowAddModal(false);
        setLimitBanner({ limit: data.limit, message: data.error });
      } else {
        alert(`Failed to add client: ${data.error}`);
      }
    } catch (err) {
      alert('Failed to add client');
    }
  };

  const handleBulkAddClients = async clients => {
    try {
      const response = await fetch(`${apiBaseUrl()}/api/clients/bulk`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clients }),
      });
      const data = await response.json();
      if (data.success) {
        fetchClients();
        if (data.limitReached) {
          setLimitBanner({ message: data.errors.find(e => e.includes('plan limit')) });
        }
      }
      return data;
    } catch (err) {
      console.error('Error bulk adding clients:', err);
      return { success: false, created: 0, skipped: 0, errors: ['Network error — please try again.'] };
    }
  };

  const handleSendListingInvite = async emailData => {
    const { listingId, subject, body } = emailData;
    const selectedListing = publishedListings.find(l => l.id.uuid === listingId);
    const listingSlug = selectedListing?.attributes?.title
      ?.replace(/\s+/g, '-').toLowerCase().replace(/[^a-z0-9-]/g, '');
    const listingUrl = `${window.location.origin}/l/${listingSlug}/${listingId}`;
    const finalBody = body.replace(/\[LISTING_LINK\]/g, listingUrl);

    try {
      const response = await fetch(`${apiBaseUrl()}/api/clients/send-listing-invite`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId: selectedClient.id,
          clientEmail: selectedClient.email,
          clientName: `${selectedClient.first_name} ${selectedClient.last_name}`,
          listingId,
          listingUrl,
          listingTitle: selectedListing?.attributes?.title,
          emailSubject: subject,
          emailBody: finalBody,
        }),
      });
      const data = await response.json();
      if (data.success) {
        alert(`✅ Invitation sent to ${selectedClient.email}!`);
        setShowListingModal(false);
        setSelectedClient(null);
      } else {
        throw new Error(data.error || 'Failed to send invitation');
      }
    } catch (err) {
      alert(`Failed to send email: ${err.message}`);
      setShowListingModal(false);
      setSelectedClient(null);
    }
  };

  const handleSyncFromTransactions = async () => {
    if (!confirm('This will import all clients from your past bookings. Continue?')) return;
    setSyncing(true);
    try {
      const response = await fetch(`${apiBaseUrl()}/api/clients/sync`, {
        method: 'POST', credentials: 'include',
      });
      const data = await response.json();
      if (data.success) {
        fetchClients();
        alert(`✅ ${data.message}\n\nNew: ${data.synced}, Updated: ${data.skipped}`);
      } else {
        throw new Error(data.error);
      }
    } catch (err) {
      alert(`Failed to sync: ${err.message}`);
    } finally {
      setSyncing(false);
    }
  };

  // CR-4: live search — filters locally on name/email/phone
  const handleSearchChange = e => {
    setSearchTerm(e.target.value);
  };

  // CR-6: sort column click
  const handleSort = key => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortIcon = key => {
    if (sortKey !== key) return <span className={css.sortNeutral}>↕</span>;
    return sortDir === 'asc' ? <span className={css.sortActive}>↑</span> : <span className={css.sortActive}>↓</span>;
  };

  // CR-7: selection handlers
  const handleToggleSelect = id => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === filteredClients.length && filteredClients.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredClients.map(c => c.id)));
    }
  };

  const handleBulkStatusChange = async newStatus => {
    const ids = [...selectedIds];
    await Promise.all(ids.map(id =>
      fetch(`${apiBaseUrl()}/api/clients/${id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      })
    ));
    setSelectedIds(new Set());
    fetchClients();
  };

  const openDmWithClient = async client => {
    if (!client.client_id) {
      alert('This client does not have a GymNet account and cannot receive DMs.');
      return;
    }
    setDmNavigating(true);
    try {
      const myDisplayName =
        `${currentUser?.attributes?.profile?.firstName || ''} ${currentUser?.attributes?.profile?.lastName || ''}`.trim() ||
        currentUser?.attributes?.profile?.displayName || 'Trainer';
      const clientDisplayName = `${client.first_name} ${client.last_name}`.trim();
      const myUUID = currentUser?.id?.uuid;
      const res = await fetch(`${apiBaseUrl()}/api/dm/conversations`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          trainerId: myUUID,
          clientId: client.client_id,
          trainerDisplayName: myDisplayName,
          clientDisplayName,
        }),
      });
      const data = await res.json();
      const convId = data.conversation?.id;
      if (convId) {
        history.push(`/inbox/sales?openDm=${convId}`);
      } else {
        history.push('/inbox/sales');
      }
    } catch {
      history.push('/inbox/sales');
    } finally {
      setDmNavigating(false);
    }
  };

  const counts = {
    all: clients.length,
    active: clients.filter(c => c.status === 'active').length,
    prospect: clients.filter(c => c.status === 'prospect').length,
    inactive: clients.filter(c => c.status === 'inactive').length,
  };

  // CR-4: local filtering on name/email/phone
  const search = searchTerm.toLowerCase().trim();
  const baseFiltered = clients.filter(c => {
    if (!search) return true;
    return (
      c.first_name?.toLowerCase().includes(search) ||
      c.last_name?.toLowerCase().includes(search) ||
      c.email?.toLowerCase().includes(search) ||
      (c.phone || '').replace(/\D/g, '').includes(search.replace(/\D/g, ''))
    );
  });

  const filteredClients = sortClients(baseFiltered, sortKey, sortDir);

  const allSelected = filteredClients.length > 0 && selectedIds.size === filteredClients.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  // Listing selection modal
  const ListingSelectionModal = ({ listings, clientName, onSelect, onClose }) => {
    const [selectedListingId, setSelectedListingId] = useState(null);
    const [emailSubject, setEmailSubject] = useState('');
    const [emailBody, setEmailBody] = useState('');
    const [step, setStep] = useState(1);
    const trainerFirstName = currentUser?.attributes?.profile?.firstName ||
      currentUser?.attributes?.profile?.displayName?.split(' ')[0];

    useEffect(() => {
      if (selectedListingId) {
        const listing = listings.find(l => l.id.uuid === selectedListingId);
        if (listing) {
          const listingTitle = listing.attributes.title;
          const firstName = clientName.split(' ')[0];
          setEmailSubject(`${listingTitle} - thought you might be interested`);
          setEmailBody(
            `Hi ${firstName},\n\nI wanted to share ${listingTitle} with you. Based on what we've discussed about your fitness goals, I think this would be a great fit.\n\nI've included the details below if you'd like to take a look. Let me know if you have any questions or want to talk through it.\n\n[LISTING_LINK]\n\n${trainerFirstName || 'Talk soon'}`
          );
        }
      }
    }, [selectedListingId]);

    return (
      <div className={css.modalOverlay} onClick={onClose}>
        <div className={css.modal} onClick={e => e.stopPropagation()}>
          <div className={css.modalHeader}>
            <h2 className={css.modalTitle}>{step === 1 ? 'Select Listing to Share' : 'Customize Email'}</h2>
            <button className={css.closeButton} onClick={onClose} aria-label="Close">×</button>
          </div>
          <div className={css.modalBody}>
            {step === 1 ? (
              <>
                <p className={css.modalDescription}>Choose which listing to send to <strong>{clientName}</strong></p>
                {listings.length === 0 ? (
                  <div className={css.emptyState}>
                    <div className={css.emptyIcon}>📋</div>
                    <h3>No active listings</h3>
                    <p>You need a published listing to send to clients.</p>
                  </div>
                ) : (
                  <div className={css.listingsList}>
                    {listings.map(listing => {
                      const title = listing.attributes.title;
                      const price = listing.attributes.price;
                      const description = listing.attributes.description;
                      let imageUrl = null;
                      if (listing.images?.length > 0) {
                        const variants = listing.images[0].attributes?.variants;
                        imageUrl = variants?.['square-small']?.url || listing.images[0].attributes?.url;
                      }
                      return (
                        <div
                          key={listing.id.uuid}
                          className={`${css.listingCard} ${selectedListingId === listing.id.uuid ? css.listingCardSelected : ''}`}
                          onClick={() => setSelectedListingId(listing.id.uuid)}
                        >
                          {imageUrl && (
                            <div className={css.listingImage}>
                              <img src={imageUrl} alt={title} onError={e => { e.target.parentElement.style.display = 'none'; }} />
                            </div>
                          )}
                          <div className={css.listingContent}>
                            <h4 className={css.listingTitle}>{title}</h4>
                            {description && <p className={css.listingDescription}>{description.length > 100 ? description.slice(0, 100) + '…' : description}</p>}
                            {price && <div className={css.listingPrice}>${(price.amount / 100).toFixed(2)} {price.currency}</div>}
                          </div>
                          <div className={css.listingCheckbox}>
                            {selectedListingId === listing.id.uuid && <span className={css.checkmark}>✓</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <>
                <p className={css.modalDescription}>Customize the email for <strong>{clientName}</strong></p>
                <div className={css.emailForm}>
                  <div className={css.formGroup}>
                    <label className={css.label}>Email Subject</label>
                    <input type="text" className={css.input} value={emailSubject} onChange={e => setEmailSubject(e.target.value)} placeholder="Enter email subject…" />
                  </div>
                  <div className={css.formGroup}>
                    <label className={css.label}>Email Body</label>
                    <textarea className={css.textarea} rows={10} value={emailBody} onChange={e => setEmailBody(e.target.value)} />
                    <p className={css.hint}>Tip: [LISTING_LINK] will be replaced with the actual listing URL</p>
                  </div>
                  <div className={css.previewBox}>
                    <h4 className={css.previewTitle}>Preview</h4>
                    <div className={css.previewContent}>
                      <strong>Subject:</strong> {emailSubject}
                      <div style={{ marginTop: '10px', whiteSpace: 'pre-wrap' }}>{emailBody}</div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          <div className={css.modalFooter}>
            {step === 1 ? (
              <>
                <button className={css.btnSecondary} onClick={onClose}>Cancel</button>
                <button className={css.btnPrimary} onClick={() => setStep(2)} disabled={!selectedListingId}>Next →</button>
              </>
            ) : (
              <>
                <button className={css.btnSecondary} onClick={() => setStep(1)}>← Back</button>
                <button className={css.btnPrimary} onClick={() => onSelect({ listingId: selectedListingId, subject: emailSubject, body: emailBody })} disabled={!emailSubject || !emailBody}>Send Email</button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Mobile helpers
  const getInitials = c => `${(c.first_name || '').charAt(0)}${(c.last_name || '').charAt(0)}`.toUpperCase();

  const getStatusStyle = status => {
    const base = { display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 10px', borderRadius: '99px', fontSize: '13px', fontWeight: 600 };
    switch (status) {
      case 'active': return { ...base, background: '#d1fae5', color: '#065f46' };
      case 'prospect': return { ...base, background: '#fef3c7', color: '#92400e' };
      case 'inactive': return { ...base, background: '#f3f4f6', color: '#6b7280' };
      default: return base;
    }
  };

  const STATUS_DOT = { active: '#10b981', prospect: '#f59e0b', inactive: '#9ca3af' };

  return (
    <div className={css.container}>
      {/* ── Client limit upgrade banner ── */}
      {limitBanner && (
        <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 10, padding: '14px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20 }}>⚠️</span>
            <span style={{ color: '#856404', fontSize: 14 }}>{limitBanner.message}</span>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button
              onClick={() => history.push('/subscription-pricing')}
              style={{ background: '#1aa6cc', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
            >
              Upgrade Plan
            </button>
            <button
              onClick={() => setLimitBanner(null)}
              style={{ background: 'none', border: '1px solid #856404', color: '#856404', borderRadius: 6, padding: '7px 12px', fontSize: 13, cursor: 'pointer' }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* ── Client Roster ── */}
      <>
          {/* Header */}
          <div className={css.header}>
            <div className={css.headerContent}>
              <h1 className={css.title}>Client Roster</h1>
              <p className={css.subtitle}>Manage your training clients and prospects</p>
            </div>
            <div className={css.headerActions}>
              <button className={css.btnSecondary} onClick={handleSyncFromTransactions} disabled={syncing} title="Import clients from past bookings">
                {syncing ? 'Syncing…' : 'Sync from Bookings'}
              </button>
              <button className={css.btnSecondary} onClick={() => setShowBulkModal(true)}>
                Bulk Import
              </button>
              <button className={css.btnPrimary} onClick={() => setShowAddModal(true)}>
                + Add Client
              </button>
            </div>
          </div>

          {/* Filter + Search bar — matches prototype layout */}
          <div className={css.filterBar}>
            <div className={css.searchWrap}>
              <span className={css.searchIcon} aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              </span>
              <input
                type="search"
                className={css.searchInput}
                placeholder="Search name, email or phone…"
                value={searchTerm}
                onChange={handleSearchChange}
                aria-label="Search clients"
              />
            </div>

            <div className={css.filterChips} role="group" aria-label="Filter by status">
              {[
                { key: 'all', label: 'All', count: counts.all, dot: null },
                { key: 'active', label: 'Active', count: counts.active, dot: '#10b981' },
                { key: 'prospect', label: 'Prospect', count: counts.prospect, dot: '#f59e0b' },
                { key: 'inactive', label: 'Inactive', count: counts.inactive, dot: '#9ca3af' },
              ].map(({ key, label, count, dot }) => (
                <button
                  key={key}
                  className={statusFilter === key ? css.chipActive : css.chip}
                  onClick={() => setStatusFilter(key)}
                  aria-pressed={statusFilter === key}
                >
                  {dot && <span className={css.chipDot} style={{ background: dot }} />}
                  {label} <span className={css.chipCount}>{count}</span>
                </button>
              ))}
              <button
                className={css.densityToggle}
                onClick={() => setDensity(d => d === 'comfortable' ? 'compact' : 'comfortable')}
                title={`Switch to ${density === 'comfortable' ? 'compact' : 'comfortable'} view`}
                aria-label="Toggle table density"
              >
                {density === 'comfortable'
                  ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
                  : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="5" x2="21" y2="5"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="13" x2="21" y2="13"/><line x1="3" y1="17" x2="21" y2="17"/><line x1="3" y1="21" x2="21" y2="21"/></svg>
                }
              </button>
            </div>
          </div>

          {/* Error */}
          {error && <div className={css.errorBox} role="alert"><span aria-hidden="true">⚠</span> {error}</div>}

          {/* Loading */}
          {loading && (
            <div className={css.loading} aria-live="polite">
              <div className={css.spinner} role="status" aria-label="Loading clients" />
              <p>Loading clients…</p>
            </div>
          )}

          {/* Client list */}
          {!loading && !error && (
            <>
              {filteredClients.length === 0 ? (
                <div className={css.emptyState}>
                  <div className={css.emptyIcon}>👥</div>
                  <h3>No clients found</h3>
                  <p>
                    {searchTerm
                      ? 'No clients match your search.'
                      : 'Add your first client to start building your roster.'}
                  </p>
                  {!searchTerm && (
                    <button className={css.btnPrimary} onClick={() => setShowAddModal(true)}>
                      Add Your First Client
                    </button>
                  )}
                </div>
              ) : (
                <>
                  {/* Desktop table */}
                  <div className={css.tableWrapper}>
                    <table className={css.clientTable} aria-label="Client roster">
                      <thead>
                        <tr>
                          <th className={css.thCheck}>
                            <input
                              type="checkbox"
                              className={css.rowCheckbox}
                              checked={allSelected}
                              ref={el => { if (el) el.indeterminate = someSelected; }}
                              onChange={handleSelectAll}
                              aria-label="Select all clients"
                            />
                          </th>
                          <th className={css.thSortable} onClick={() => handleSort('last_name')} aria-sort={sortKey === 'last_name' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                            Client {sortIcon('last_name')}
                          </th>
                          <th className={css.thSortable} onClick={() => handleSort('status')} aria-sort={sortKey === 'status' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                            Status {sortIcon('status')}
                          </th>
                          <th className={css.thSortable} onClick={() => handleSort('active_programs')} aria-sort={sortKey === 'active_programs' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                            Programs {sortIcon('active_programs')}
                          </th>
                          <th className={css.thSortable} onClick={() => handleSort('last_workout_date')} aria-sort={sortKey === 'last_workout_date' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                            Last Workout {sortIcon('last_workout_date')}
                          </th>
                          <th>Phone</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredClients.map(client => (
                          <ClientCard
                            key={client.id}
                            client={client}
                            onUpdate={fetchClients}
                            currentUser={currentUser}
                            onOpenListingModal={c => { setSelectedClient(c); setShowListingModal(true); }}
                            onSendDm={openDmWithClient}
                            selected={selectedIds.has(client.id)}
                            onToggleSelect={handleToggleSelect}
                            density={density}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* CR-9: footer count */}
                  <p className={css.tableFooter} aria-live="polite">
                    Showing {filteredClients.length} of {clients.length} client{clients.length !== 1 ? 's' : ''}
                    {selectedIds.size > 0 && ` · ${selectedIds.size} selected`}
                  </p>

                  {/* Mobile card list */}
                  <div className={css.mobileList} aria-label="Client roster">
                    {filteredClients.map(client => (
                      <div key={client.id} className={css.mobileCard}>
                        <div className={css.mobileCardHeader}>
                          <div className={css.mobileAvatar}>{getInitials(client)}</div>
                          <div className={css.mobileNameBlock}>
                            <p className={css.mobileName}>{client.first_name} {client.last_name}</p>
                            <p className={css.mobileEmail}>{client.email}</p>
                          </div>
                          <div className={css.mobileStatus}>
                            <span style={getStatusStyle(client.status)}>
                              <span style={{ width: 6, height: 6, borderRadius: '50%', background: STATUS_DOT[client.status] || '#9ca3af', display: 'inline-block' }} />
                              {client.status.charAt(0).toUpperCase() + client.status.slice(1)}
                            </span>
                          </div>
                        </div>
                        <div className={css.mobileCardBody}>
                          <div className={css.mobileField}>
                            <span className={css.mobileFieldLabel}>Programs</span>
                            <span className={css.mobileFieldValue}>{client.active_programs || 0}</span>
                          </div>
                          <div className={css.mobileField}>
                            <span className={css.mobileFieldLabel}>Last Workout</span>
                            <span className={css.mobileFieldValue}>
                              {client.last_workout_date ? new Date(client.last_workout_date).toLocaleDateString() : 'None'}
                            </span>
                          </div>
                          <div className={css.mobileField}>
                            <span className={css.mobileFieldLabel}>Phone</span>
                            <span className={css.mobileFieldValue}>{client.phone || '—'}</span>
                          </div>
                          <div className={css.mobileField}>
                            <span className={css.mobileFieldLabel}>Member Since</span>
                            <span className={css.mobileFieldValue}>
                              {client.created_at ? new Date(client.created_at).toLocaleDateString() : '—'}
                            </span>
                          </div>
                        </div>
                        <button className={css.mobileDetailsBtn} onClick={() => history.push(`/clients/${client.id}`)}>
                          View Details →
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          {/* Sticky bulk action bar */}
          {selectedIds.size > 0 && (
            <div className={css.bulkBar} role="toolbar" aria-label="Bulk actions">
              <span className={css.bulkCount}>{selectedIds.size} selected</span>
              <div className={css.bulkActions}>
                <button
                  className={css.bulkBtn}
                  onClick={() => setShowEmailCampaign(true)}
                >
                  Send Email Campaign
                </button>
                {selectedIds.size === 1 && (() => {
                  const singleClient = filteredClients.find(c => selectedIds.has(c.id));
                  return singleClient?.client_id ? (
                    <button
                      className={css.bulkBtn}
                      onClick={() => openDmWithClient(singleClient)}
                      disabled={dmNavigating}
                    >
                      {dmNavigating ? 'Opening…' : 'Send DM'}
                    </button>
                  ) : null;
                })()}
                <button className={css.bulkBtn} onClick={() => handleBulkStatusChange('active')}>Mark active</button>
                <button className={css.bulkBtn} onClick={() => handleBulkStatusChange('inactive')}>Mark inactive</button>
                <button className={css.bulkBtnCancel} onClick={() => setSelectedIds(new Set())}>Clear</button>
              </div>
            </div>
          )}
      </>

      {/* Add Client Modal */}
      {showAddModal && (
        <AddClientModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onAddClient={handleAddClient}
        />
      )}

      {/* Bulk Import Modal */}
      {showBulkModal && (
        <BulkAddClientModal
          isOpen={showBulkModal}
          onClose={() => setShowBulkModal(false)}
          onBulkAdd={handleBulkAddClients}
        />
      )}

      {/* Listing Selection Modal */}
      {showListingModal && selectedClient && (
        <ListingSelectionModal
          listings={publishedListings}
          clientName={`${selectedClient.first_name} ${selectedClient.last_name}`}
          onSelect={handleSendListingInvite}
          onClose={() => { setShowListingModal(false); setSelectedClient(null); }}
        />
      )}

      {/* Email Campaign Modal */}
      {showEmailCampaign && (
        <EmailCampaignModal
          clients={filteredClients.filter(c => selectedIds.has(c.id))}
          onClose={() => setShowEmailCampaign(false)}
        />
      )}
    </div>
  );
};

export default ClientList;
